'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const { Component } = React;
const classnames = require('classnames');
const LazyLoad = require('react-lazy-load').default;

const SV = require('server-vars');
const deviceTypeEnum = require('../../../global/utils/deviceTypeEnum');

const ModalCarouselTracking = require('./ModalCarouselTracking');
const PdpActions = require('../../actions/PdpActions');
const CarouselItem = require('./CarouselItem');
const CarouselItemRequestMoreImages = require('./RequestMoreImages/CarouselItemRequestMoreImages')
    .default;
const { ArrowCarousel, SwipeCarousel } = require('../../../global/components/carousel');
const ZoomModal = require('./ZoomModal');
const pdpTrackingActions = require('../../actions/pdpTrackingActions.es');

/**
 * Carousel for the PDP hero.
 * @class
 */
class Carousel extends Component {
    constructor(props) {
        super(props);
        this._handleImageChange = this._handleImageChange.bind(this);
        this._onThumbnailClick = this._onThumbnailClick.bind(this);
        this._updatePage = this._updatePage.bind(this);
        this._closeModal = this._closeModal.bind(this);
        this._onZoomModalLeftArrowClick = this._onZoomModalLeftArrowClick.bind(this);
        this._onZoomModalRightArrowClick = this._onZoomModalRightArrowClick.bind(this);

        this._tracking = new ModalCarouselTracking({
            owner: props.owner,
            imagesLength: props.images.length,
        });

        this.state = {
            index: 0,
            modalOpen: props.isStaticModal,
        };
    }

    componentWillMount() {
        if (this.props.isStaticModal) {
            this._tracking.showModal(this.state.index);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // fire event only when opening the modal
        if (this.state.modalOpen && !prevState.modalOpen) {
            this._tracking.showModal(this.state.index);
        }
    }

    _handleImageChange(index) {
        // Fire an event on page change in case other parts of the page need to respond.
        this.context.executeAction(PdpActions.setIndex, { index });
        // Re-render when the page changes.
        this.setState({ index });
    }

    _getChildren(deviceType) {
        const {
            images,
            onContactDealerClick,
            isRemoveContactDealerCTATestRunning,
            itemId,
        } = this.props;
        const className = classnames('pdp-hero-carousel-image', {
            'is-zoom': !deviceType.isMobile(),
        });

        const carouselItems = images.map((image, index) => {
            const imageProps = {
                alt: image.alt,
                className,
                src: image.large.src,
                index,
                onClick: () => this.setState({ modalOpen: true }),
            };

            if (index === 0) {
                return <CarouselItem {...imageProps} key={index} />;
            }

            return (
                <LazyLoad className="pdp-hero-carousel-lazy" offsetHorizontal={300} key={index}>
                    <CarouselItem {...imageProps} />
                </LazyLoad>
            );
        });

        carouselItems.push(
            <CarouselItemRequestMoreImages
                key={'carouselItemRequestMoreImages'}
                onContactDealerClick={onContactDealerClick}
                itemId={itemId}
                isRemoveContactDealerCTATestRunning={isRemoveContactDealerCTATestRunning}
            />
        );

        return carouselItems;
    }

    _getCarousel(deviceType) {
        const { style, index } = this.props;
        const ref = 'inner';
        const itemsPerPage = 1;
        const onPageChange = this._handleImageChange;
        const classNames = { list: 'pdp-hero-carousel-list' };
        let props = {};
        let carouselType;

        if (
            window &&
            'ontouchstart' in window &&
            (deviceType.isMobile() || deviceType.isTablet())
        ) {
            carouselType = SwipeCarousel;
        } else {
            carouselType = ArrowCarousel;
            props = {
                onRightArrowClick: this._onRightArrowClick.bind(this),
                onLeftArrowClick: this._onLeftArrowClick.bind(this),
            };
        }

        return React.createElement(
            carouselType,
            Object.assign({}, props, {
                children: this._getChildren(deviceType),
                style,
                ref,
                itemsPerPage,
                classNames,
                onPageChange,
                startingPage: index,
            })
        );
    }

    getCustomTrackingLabel(page) {
        return page === this.props.images.length ? 'contact dealer - pdp image carousel' : null;
    }

    _onRightArrowClick(page) {
        this.context.executeAction(pdpTrackingActions.trackNextImageClick, {
            image: page + 1, // `page` is zero-indexed
            totalImages: this.props.images.length,
            customLabel: this.getCustomTrackingLabel(page),
        });
    }

    _onLeftArrowClick(page) {
        this.context.executeAction(pdpTrackingActions.trackPreviousImageClick, {
            image: page + 1, // `page` is zero-indexed
            totalImages: this.props.images.length,
            customLabel: this.getCustomTrackingLabel(page),
        });
    }

    _onZoomModalRightArrowClick(page) {
        this._tracking.nextClick(page);
    }

    _onZoomModalLeftArrowClick(page) {
        this._tracking.previousClick(page);
    }

    _onThumbnailClick(page) {
        this._tracking.thumbnailClick(page);
    }

    _updatePage(page) {
        this.refs.inner.setPage(page);
    }

    _closeModal() {
        const { onCloseModal } = this.props;
        if (onCloseModal) {
            onCloseModal();
        } else {
            this.setState({ modalOpen: false });
        }
    }

    render() {
        const { index, modalOpen } = this.state;
        const { isDesktop, isTablet } = SV.get('settings');
        const {
            context,
            owner,
            wrapperClasses,
            images,
            itemId,
            title,
            heartComponent,
            socialData,
            style,
            isPrintPage,
            onContactDealerClick,
            selectedItems,
            relay,
            itemData,
        } = this.props;

        return (
            <div className="pdp-hero-carousel" data-tn="pdp-image-carousel">
                {/* Remove mobile carousel as this will be handeled by adaptive pdp in abf. */}

                {/* If we are on a tablet device, use the swiping version of the carousel. */}
                {isTablet && this._getCarousel(deviceTypeEnum.tablet())}
                {/* If we are on desktop, use the version of the carousel with arrows. */}
                {isDesktop && this._getCarousel(deviceTypeEnum.desktop())}
                <span className="pdp-hero-carousel-indicator">
                    {/* Image counter is increased as the last item is request more images view */}
                    {`${index + 1} of ${images.length + 1} images`}
                </span>
                {/* Remove mobile zoom modal as this will be handeled by adaptive pdp in abf. */}
                {!isPrintPage && (
                    <ZoomModal
                        context={context}
                        owner={owner}
                        heartComponent={heartComponent}
                        wrapperClasses={wrapperClasses}
                        overlayClassName="modal-carousel-overlay"
                        isOpen={modalOpen}
                        style={style}
                        itemId={itemId}
                        socialData={socialData}
                        selectedItems={selectedItems}
                        relay={relay}
                        title={title}
                        images={images}
                        startingIndex={index}
                        onThumbnailClick={this._onThumbnailClick}
                        onIndexChange={this._updatePage}
                        onRequestClose={this._closeModal}
                        onLeftArrowClick={this._onZoomModalLeftArrowClick}
                        onRightArrowClick={this._onZoomModalRightArrowClick}
                        onContactDealerClick={onContactDealerClick}
                        itemData={itemData}
                    />
                )}
            </div>
        );
    }
}

Carousel.propTypes = {
    context: PropTypes.object,
    owner: PropTypes.string,
    heartComponent: PropTypes.object,
    wrapperClasses: PropTypes.string,
    isStaticModal: PropTypes.bool,
    style: PropTypes.object,
    mobileStyle: PropTypes.object,
    socialData: PropTypes.object,
    images: PropTypes.array.isRequired,
    item: PropTypes.object.isRequired,
    itemId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    onCloseModal: PropTypes.func,
    isPrintPage: PropTypes.bool,
    index: PropTypes.number.isRequired,
    onContactDealerClick: PropTypes.func,
    isRemoveContactDealerCTATestRunning: PropTypes.bool,
    selectedItems: PropTypes.array,
    relay: PropTypes.object,
    itemData: PropTypes.object,
};

Carousel.defaultTypes = {
    isStaticModal: false,
};

Carousel.contextTypes = {
    executeAction: PropTypes.func.isRequired,
    getStore: PropTypes.func.isRequired,
};

module.exports = Carousel;



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Carousel/Carousel.jsx